/**
 * PipelineCard - Individual job card in the Kanban pipeline.
 * Uses @dnd-kit/core useDraggable — works on both mouse and touch (mobile).
 * Shows job title, company, match score badge, date added.
 * Hover reveals quick action buttons.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { PipelineJob, PipelineStatus } from '@/types/pipeline';

interface PipelineCardProps {
  job: PipelineJob;
  onMoveToNext: (jobId: string) => void;
  onDelete: (jobId: string) => void;
  /** When true, this card is rendered inside DragOverlay (floating copy) */
  isOverlay?: boolean;
}

/** Status progression order for the "move to next" action */
const NEXT_STATUS: Record<PipelineStatus, PipelineStatus | null> = {
  saved: 'applied',
  applied: 'interview',
  interview: 'offer',
  offer: null,
  rejected: null,
};

export function PipelineCard({ job, onMoveToNext, onDelete, isOverlay }: PipelineCardProps) {
  const navigate = useNavigate();

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: job.id,
    disabled: isOverlay,
  });

  // Don't apply transform on the original card — DragOverlay handles the visual movement.
  // Only the overlay copy needs no transform (it's positioned by dnd-kit automatically).
  const style = !isOverlay && transform
    ? { transform: CSS.Translate.toString(transform) }
    : undefined;

  const handleClick = useCallback(() => {
    navigate(`/app/pipeline/${job.id}`);
  }, [navigate, job.id]);

  const handleMoveNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onMoveToNext(job.id);
    },
    [job.id, onMoveToNext]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(job.id);
    },
    [job.id, onDelete]
  );

  const nextStatus = NEXT_STATUS[job.status];

  const scoreClass =
    job.matchScore > 70
      ? 'pipeline-card__score--high'
      : job.matchScore > 50
        ? 'pipeline-card__score--medium'
        : 'pipeline-card__score--low';

  const formattedDate = formatRelativeDate(job.addedAt);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`pipeline-card ${isDragging ? 'pipeline-card--dragging' : ''} ${isOverlay ? 'pipeline-card--overlay' : ''}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleClick();
        }
      }}
      {...listeners}
      {...attributes}
    >
      {/* Quick actions (visible on hover / always on touch) */}
      <div className="pipeline-card__actions">
        {nextStatus && (
          <button
            className="pipeline-card__action-btn pipeline-card__action-btn--next"
            onClick={handleMoveNext}
            title={`Move to ${nextStatus}`}
            aria-label={`Move to ${nextStatus}`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        )}
        <button
          className="pipeline-card__action-btn pipeline-card__action-btn--delete"
          onClick={handleDelete}
          title="Delete job"
          aria-label="Delete job"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* Title */}
      <h4 className="pipeline-card__title" title={job.title}>
        {job.title}
      </h4>

      {/* Company */}
      <p className="pipeline-card__company" title={job.company}>
        {job.company}
      </p>

      {/* Footer: date + score */}
      <div className="pipeline-card__footer">
        <span className="pipeline-card__date">{formattedDate}</span>
        {job.matchScore > 0 && (
          <span className={`pipeline-card__score ${scoreClass}`}>{job.matchScore}%</span>
        )}
      </div>
    </div>
  );
}

/**
 * Format an ISO date string to a concise relative date.
 */
function formatRelativeDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return date.toLocaleDateString();
}
